import lottie, {
  type AnimationItem,
} from 'lottie-web/build/player/lottie_light'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import appLoaderAnimation from 'assets/lottie/app-loader.json'

/**
 * The branded loading animation shown by GlobalBootstrapOverlay. Split out
 * so the overlay's always-mounted <div> only ever swaps this element's
 * `playing` prop, never the element itself.
 *
 * The overlay never unmounts (see GlobalBootstrapOverlay's docstring), so a
 * hidden loader would otherwise keep animating for the whole session.
 * pause()/play() resume from the current frame rather than restarting, so
 * toggling `playing` costs no visual glitch.
 */
export const AppLoader: React.FC<{ playing: boolean }> = ({ playing }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<AnimationItem | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const animation = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: appLoaderAnimation,
    })
    animationRef.current = animation

    return () => {
      animation.destroy()
      animationRef.current = null
    }
  }, [])

  useEffect(() => {
    if (playing) animationRef.current?.play()
    else animationRef.current?.pause()
  }, [playing])

  return <Canvas ref={containerRef} role="status" aria-label="Loading" />
}

const Canvas = styled.div`
  width: 80px;
  height: 80px;
`
